import {
  REFRESH_TRANSPORT_HEADER,
  SESSION_KIND_HEADER,
  SESSION_KINDS,
  type ApiErrorEnvelope,
  type IssuedTokens,
  type SessionKind,
} from '@his/shared';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const TENANT_KEY = 'his.tenant';
export const BRANCH_KEY = 'his.branch';
const ACCESS_KEY = 'his.access';
const LEGACY_REFRESH_KEY = 'his.refresh';
const IMP_ACCESS_KEY = 'his.imp.access';
const IMP_TENANT_KEY = 'his.imp.tenant';
const IMP_BRANCH_KEY = 'his.imp.branch';

function tabRead(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function tabWrite(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    return;
  }
}

function impersonating(): boolean {
  return Boolean(tabRead(IMP_ACCESS_KEY));
}

function sessionKindOf(token: string | null): SessionKind | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const typ = (JSON.parse(atob(padded)) as { typ?: unknown }).typ;
    return (SESSION_KINDS as readonly unknown[]).includes(typ) ? (typ as SessionKind) : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: { path?: string; message: string }[] = [],
    readonly requestId?: string,
    readonly localised?: { messageKey?: string; params?: Record<string, string | number> },
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, for react-hook-form's setError. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.path) out[detail.path] = detail.message;
    }
    return out;
  }
}

export const session = {
  tenant: (): string | null => (impersonating() ? tabRead(IMP_TENANT_KEY) : localStorage.getItem(TENANT_KEY)),
  setTenant: (slug: string | null) =>
    slug ? localStorage.setItem(TENANT_KEY, slug) : localStorage.removeItem(TENANT_KEY),
  /** Branch the user is working in. Null means every branch they can reach. */
  branch: (): string | null => (impersonating() ? tabRead(IMP_BRANCH_KEY) : localStorage.getItem(BRANCH_KEY)),
  setBranch: (branchId: string | null) => {
    if (impersonating()) return tabWrite(IMP_BRANCH_KEY, branchId);
    return branchId ? localStorage.setItem(BRANCH_KEY, branchId) : localStorage.removeItem(BRANCH_KEY);
  },
  access: (): string | null => tabRead(IMP_ACCESS_KEY) ?? localStorage.getItem(ACCESS_KEY),
  kind: (): SessionKind | null => (impersonating() ? null : sessionKindOf(localStorage.getItem(ACCESS_KEY))),
  canRefresh: (): boolean => session.kind() !== null,
  legacyRefresh: (): string | null => (impersonating() ? null : localStorage.getItem(LEGACY_REFRESH_KEY)),
  impersonating,
  setImpersonation: (value: { accessToken: string; tenant: string } | null) => {
    tabWrite(IMP_ACCESS_KEY, value?.accessToken ?? null);
    tabWrite(IMP_TENANT_KEY, value?.tenant ?? null);
    tabWrite(IMP_BRANCH_KEY, null);
  },
  setTokens: (tokens: Pick<IssuedTokens, 'accessToken'> | null) => {
    localStorage.removeItem(LEGACY_REFRESH_KEY);
    if (!tokens) {
      localStorage.removeItem(ACCESS_KEY);
      return;
    }
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
  },
  clear: () => {
    if (impersonating()) {
      tabWrite(IMP_ACCESS_KEY, null);
      tabWrite(IMP_TENANT_KEY, null);
      tabWrite(IMP_BRANCH_KEY, null);
      return;
    }
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
    // The next person to sign in on this machine is often at a different desk.
    localStorage.removeItem(BRANCH_KEY);
  },
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent on payment and dispensing calls so a double submit cannot double-charge. */
  idempotencyKey?: string;
  /** Reason for a break-the-glass access; recorded in the audit trail. */
  breakGlassReason?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * A single refresh runs at a time. Without this, a screen that fires six
 * queries on mount would send six refreshes on an expired token, and the
 * server's reuse detection would treat five of them as a leak and end the
 * session — logging the user out for loading a page.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Outcome of a token refresh.
 *
 * `fatal` is the point of this type: it separates "the server looked at these
 * credentials and said no" from "the server could not answer right now". A 429
 * from the rate limiter, a 5xx, or an offline workstation must NOT end the
 * session — treating them as a sign-out is what throws staff back to the login
 * screen mid-shift for reasons they cannot see.
 */
type RefreshOutcome = { ok: true } | { ok: false; fatal: boolean };

function isRejectedSignIn(path: string): boolean {
  return path.startsWith('/auth/') && path.includes('login');
}

const REFRESH_LOCK = 'his.refresh-lock';
const REFRESH_CHANNEL = 'his.refresh';
const PEER_REFRESH_WAIT_MS = 10_000;

let peerChannel: BroadcastChannel | null | undefined;
let peerRefreshUntil = 0;
const peerWaiters = new Set<() => void>();

function webLocks(): LockManager | null {
  try {
    const locks = (globalThis.navigator as Navigator | undefined)?.locks;
    return locks && typeof locks.request === 'function' ? locks : null;
  } catch {
    return null;
  }
}

function wakePeerWaiters(): void {
  peerRefreshUntil = 0;
  for (const wake of [...peerWaiters]) wake();
}

function refreshChannel(): BroadcastChannel | null {
  if (peerChannel !== undefined) return peerChannel;
  if (typeof BroadcastChannel !== 'function') {
    peerChannel = null;
    return peerChannel;
  }
  try {
    const channel = new BroadcastChannel(REFRESH_CHANNEL);
    (channel as unknown as { unref?: () => void }).unref?.();
    channel.onmessage = (event: MessageEvent) => {
      if (event.data === 'start') peerRefreshUntil = Date.now() + PEER_REFRESH_WAIT_MS;
      if (event.data === 'done') wakePeerWaiters();
    };
    peerChannel = channel;
  } catch {
    peerChannel = null;
  }
  return peerChannel;
}

function waitForPeerRefresh(): Promise<void> {
  const remaining = peerRefreshUntil - Date.now();
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      peerWaiters.delete(finish);
      resolve();
    };
    const timer = setTimeout(finish, remaining);
    peerWaiters.add(finish);
  });
}

async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = webLocks();
  if (locks) return locks.request(REFRESH_LOCK, () => work()) as Promise<T>;
  const peers = refreshChannel();
  if (!peers) return work();
  await waitForPeerRefresh();
  peers.postMessage('start');
  try {
    return await work();
  } finally {
    peers.postMessage('done');
  }
}

if (!webLocks()) refreshChannel();

function rotatedElsewhere(presented: string | null, staleAccess: string | null | undefined): boolean {
  if (session.legacyRefresh()) return false;
  const current = session.access();
  if (!current || !session.kind()) return false;
  if (presented && current !== presented) return true;
  return Boolean(staleAccess) && current !== staleAccess;
}

function refreshRequest(kind: SessionKind, legacy: string | null): RequestInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', [SESSION_KIND_HEADER]: kind, ...tenantHeader() };
  if (legacy) headers[REFRESH_TRANSPORT_HEADER] = 'upgrade';
  return {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(legacy ? { refreshToken: legacy } : {}),
  };
}

async function refreshTokens(staleAccess?: string | null): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    const presented = session.access();
    refreshInFlight = withRefreshLock(async (): Promise<RefreshOutcome> => {
      if (rotatedElsewhere(presented, staleAccess)) return { ok: true };
      const kind = session.kind();
      if (!kind) return { ok: false, fatal: true };
      try {
        const response = await fetch(`${BASE}/auth/refresh`, refreshRequest(kind, session.legacyRefresh()));
        if (!response.ok) {
          // 401/403 = genuinely rejected. 429 (rate limited), 423 (temporarily
          // suspended) and 5xx = "not now" — keep the session.
          return { ok: false, fatal: response.status === 401 || response.status === 403 };
        }
        session.setTokens((await response.json()) as IssuedTokens);
        return { ok: true };
      } catch {
        // Network/DNS/offline — transient by definition.
        return { ok: false, fatal: false };
      }
    })
      .catch((): RefreshOutcome => ({ ok: false, fatal: false }))
      .finally(() => {
        // Cleared on the next tick so concurrent callers all observe this result.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });
  }
  return refreshInFlight;
}

export async function migrateLegacySession(): Promise<void> {
  if (!session.legacyRefresh()) return;
  if (!session.kind()) {
    session.setTokens(null);
    return;
  }
  const outcome = await refreshTokens();
  if (!outcome.ok && outcome.fatal) session.clear();
}

export function sessionHeaders(): Record<string, string> {
  const kind = session.kind();
  return kind ? { [SESSION_KIND_HEADER]: kind } : {};
}

function tenantHeader(): Record<string, string> {
  const slug = session.tenant();
  return slug ? { 'X-Tenant-Id': slug } : {};
}

/**
 * The branch the user picked in the switcher.
 *
 * Sent as a hint, never as an authority: the server checks it against what the
 * caller's roles actually allow and refuses anything wider, so a tampered value
 * buys nothing.
 */
function branchHeader(): Record<string, string> {
  const branchId = session.branch();
  return branchId ? { 'X-Branch-Id': branchId } : {};
}

/**
 * Reads a response body that is supposed to be JSON.
 *
 * When `/api` is not routed to the API — a proxy pointed at a port nothing is
 * listening on, a reverse proxy that has not been reloaded, a dev server
 * answering every path with the SPA shell — the reply is `index.html`, and
 * parsing it raised `Unexpected token '<'` on whatever screen the user was on.
 * That message says nothing about the actual problem, so it is turned into one
 * that does.
 */
function parseJsonBody(text: string, status: number, path: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    const looksLikeHtml = /^\s*<(!doctype|html)/i.test(text);
    throw new ApiError(
      status,
      'bad_gateway',
      looksLikeHtml
        ? `The server returned a web page instead of data for ${path}. The API is not reachable behind /api — check that it is running and that the proxy points at it.`
        : `The server returned something that is not JSON for ${path} (HTTP ${status}).`,
    );
  }
}

const STEP_UP_REFUSALS = new Set(['STEP_UP_FAILED', 'STEP_UP_REPLAYED', 'STEP_UP_NOT_ENROLLED']);

export async function isStepUpRefusal(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as ApiErrorEnvelope | null;
    return STEP_UP_REFUSALS.has(body?.error?.code ?? '');
  } catch {
    return false;
  }
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await request<T>(path, options)).payload;
}

export interface CursorPage<T> {
  rows: T[];
  nextCursor: string | null;
}

export async function getCursorPage<T>(path: string, signal?: AbortSignal): Promise<CursorPage<T>> {
  const { payload, response } = await request<T[]>(path, { signal });
  return { rows: payload ?? [], nextCursor: response.headers.get('X-Next-Cursor') };
}

async function request<T>(path: string, options: RequestOptions): Promise<{ payload: T; response: Response }> {
  let sentAccess: string | null = null;
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { ...tenantHeader(), ...branchHeader() };
    const access = session.access();
    sentAccess = access;
    if (access) headers.Authorization = `Bearer ${access}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
    if (options.breakGlassReason) headers['X-Break-Glass-Reason'] = options.breakGlassReason;
    Object.assign(headers, options.headers ?? {});

    return fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      ...(path.startsWith('/auth/') ? { credentials: 'include' as const } : {}),
    });
  };

  let response = await send();

  // An expired access token is normal every fifteen minutes; refresh once and
  // replay rather than surfacing it to the user.
  if (response.status === 401 && session.canRefresh() && !isRejectedSignIn(path) && !(await isStepUpRefusal(response))) {
    const outcome = await refreshTokens(sentAccess);
    if (outcome.ok) {
      response = await send();
    } else if (outcome.fatal) {
      session.clear();
      window.dispatchEvent(new CustomEvent('his:signed-out'));
    }
    // Transient failure: keep the session and let the original 401 surface as
    // an ordinary request error, so the next attempt recovers on its own.
  }

  if (response.status === 204) return { payload: undefined as T, response };

  const text = await response.text();
  const payload = parseJsonBody(text, response.status, path);

  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'internal_error',
      envelope?.error?.message ?? response.statusText,
      envelope?.error?.details ?? [],
      envelope?.error?.requestId,
      envelope?.error ? { messageKey: envelope.error.messageKey, params: envelope.error.params } : undefined,
    );
  }

  return { payload: payload as T, response };
}

/**
 * Posts a request whose answer is a file, and hands it to the browser.
 *
 * `api()` always parses JSON, which a workbook is not. The token is refreshed
 * first rather than after a 401, because a failed download leaves the user
 * with nothing to retry — no error body reaches them, just a file that never
 * arrived.
 */
export async function downloadGet(path: string, filename: string): Promise<void> {
  if (session.canRefresh()) await refreshTokens();
  const headers: Record<string, string> = { ...tenantHeader(), ...branchHeader() };
  const access = session.access();
  if (access) headers.Authorization = `Bearer ${access}`;
  const response = await fetch(`${BASE}${path}`, { headers });
  if (!response.ok) {
    const text = await response.text();
    const envelope = parseJsonBody(text, response.status, path) as ApiErrorEnvelope | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'internal_error',
      envelope?.error?.message ?? response.statusText,
      envelope?.error?.details ?? [],
      envelope?.error?.requestId,
      envelope?.error ? { messageKey: envelope.error.messageKey, params: envelope.error.params } : undefined,
    );
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadFile(path: string, body: unknown, filename: string): Promise<void> {
  if (session.canRefresh()) await refreshTokens();

  const headers: Record<string, string> = {
    ...tenantHeader(),
    ...branchHeader(),
    'Content-Type': 'application/json',
  };
  const access = session.access();
  if (access) headers.Authorization = `Bearer ${access}`;

  const response = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!response.ok) {
    const text = await response.text();
    const envelope = parseJsonBody(text, response.status, path) as ApiErrorEnvelope | null;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'internal_error',
      envelope?.error?.message ?? response.statusText,
      envelope?.error?.details ?? [],
      envelope?.error?.requestId,
      envelope?.error ? { messageKey: envelope.error.messageKey, params: envelope.error.params } : undefined,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Multipart upload with progress (the owner console's release upload — a
 * `.lpk` can be 2 GB). `fetch` cannot report upload progress, so this is the
 * one XMLHttpRequest in the client. The access token is refreshed first: a
 * long upload must not straddle the fifteen-minute token lifetime.
 */
export async function uploadWithProgress<T>(path: string, form: FormData, onProgress: (fraction: number) => void): Promise<T> {
  if (session.canRefresh()) await refreshTokens();
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    const access = session.access();
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);
    for (const [key, value] of Object.entries({ ...tenantHeader(), ...branchHeader() })) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(Math.min(1, event.loaded / event.total));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network_error', 'The upload was interrupted'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'The upload was cancelled'));
    xhr.onload = () => {
      const text = xhr.responseText;
      let payload: unknown = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload as T);
        return;
      }
      const envelope = payload as ApiErrorEnvelope | null;
      reject(
        new ApiError(
          xhr.status,
          envelope?.error?.code ?? 'internal_error',
          envelope?.error?.message ?? xhr.statusText,
          envelope?.error?.details ?? [],
          envelope?.error?.requestId,
          envelope?.error ? { messageKey: envelope.error.messageKey, params: envelope.error.params } : undefined,
        ),
      );
    };
    xhr.send(form);
  });
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
  api<T>(path, { ...options, method: 'POST', body });
export const put = <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
  api<T>(path, { ...options, method: 'PUT', body });
export const patch = <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
  api<T>(path, { ...options, method: 'PATCH', body });
export const del = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'DELETE' });

/** Builds a query string, dropping empty values so URLs stay readable. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
